import { Media } from "./Media";
import { trackDemoClick } from "@/lib/telemetry";
import { Button } from "./ui/button";
import { NewsletterSignup } from "./NewsletterSignup";
import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Linkedin, Twitter, Youtube } from "lucide-react";
import logo from "@/assets/datasyte-logo-white.webp";

export const Footer = () => {
  const exploreLinks = [
    { href: "/how-it-works", label: "How it Works" },
    { href: "/train", label: "Train" },
    { href: "/benefits", label: "Benefits" },
    { href: "/video", label: "Video" },
    { href: "/problem", label: "Problem" },
    { href: "/aimediq", label: "AIMediQ™" },
    { href: "/technology", label: "Technology" },
    { href: "/competition", label: "Competition" },
    { href: "/market", label: "Market" },
    { href: "/business-model", label: "Model" },
    { href: "/guardrails", label: "Guardrails" },
  ];

  return (
    <footer className="band-dark py-14 mt-10 border-t border-border">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-[2fr_1fr_1fr] gap-8">
          <div>
            <Media
              src={logo}
              alt="datasyteAI™ by PathFree Technologies logo"
              className="h-14 w-auto object-contain mb-4"
              sizes="230px"
            />
            <p className="text-sm text-muted-foreground mb-3">
              Expert-trained decision support for Code Blue—aligned to your best practice, with quantum-resistant security in progress.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              <strong>AIMediQ™</strong> — the AI-powered Code Cart that brings datasyteAI™ intelligence to the bedside.
            </p>
            <h4 className="font-bold mb-3">Stay in the loop</h4>
            <NewsletterSignup />
          </div>

          <div>
            <h4 className="font-bold mb-4">Explore</h4>
            <div className="flex flex-col gap-2">
              {exploreLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm text-accent hover:underline inline-flex items-center min-h-12 py-2"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-bold mb-4">Contact</h4>
            <Button
              asChild
              size="lg"
              className="w-full mb-4 h-14 text-base font-bold shadow-lg shadow-primary/20"
            >
              <a
                href="https://pathfree.com/contact"
                onClick={() => trackDemoClick("footer")}
              >
                Schedule a Demo
                <ArrowRight className="ml-2" size={18} />
              </a>
            </Button>
            <div className="text-sm">
              <p className="inline-flex items-center flex-wrap min-h-12 py-1">
                <strong className="mr-1">Email:</strong>{" "}
                <a href="mailto:info@pathfree.com" className="text-accent hover:underline inline-flex items-center min-h-12 py-2 px-1 -ml-1">
                  info@pathfree.com
                </a>
              </p>
              <p className="inline-flex items-center flex-wrap min-h-12 py-1">
                <strong className="mr-1">Phone:</strong>{" "}
                <a href="tel:+19499033788" className="text-accent hover:underline inline-flex items-center min-h-12 py-2 px-1 -ml-1">
                  +1 (949) 903-3788
                </a>
              </p>
              <p className="text-muted-foreground py-1">
                <strong>Address:</strong> 19800 McArthur Blvd Unit 300, Irvine, CA 92612, USA
              </p>
              <h4 className="font-bold mt-6 mb-3">Follow</h4>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.linkedin.com/in/pathfreetech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="PathFree on LinkedIn"
                  className="inline-flex items-center justify-center h-12 w-12 rounded-full border border-border text-accent hover:bg-accent/10 transition-colors"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href="https://x.com/PathFreeTech"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="PathFree on X"
                  className="inline-flex items-center justify-center h-12 w-12 rounded-full border border-border text-accent hover:bg-accent/10 transition-colors"
                >
                  <Twitter size={20} />
                </a>
                <a
                  href="https://www.facebook.com/drperry.m.brunettephd"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="PathFree on Facebook"
                  className="inline-flex items-center justify-center h-12 w-12 rounded-full border border-border text-accent hover:bg-accent/10 transition-colors"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://www.youtube.com/channel/UCgedxHJUM_TIIKmJWo0B19w"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="PathFree on YouTube"
                  className="inline-flex items-center justify-center h-12 w-12 rounded-full border border-border text-accent hover:bg-accent/10 transition-colors"
                >
                  <Youtube size={20} />
                </a>
              </div>
              <p className="text-muted-foreground text-xs mt-4">
                &copy; 2026 PathFree Technologies Corporation | Irvine, California
              </p>
              <div className="flex flex-col gap-2 mt-2">
                <a
                  href="https://pathfree.com"
                  className="text-accent hover:underline text-xs inline-flex items-center min-h-12 py-3"
                >
                  Visit PathFree.com
                </a>
                <Link to="/privacy" className="text-accent hover:underline text-xs inline-flex items-center min-h-12 py-3">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-accent hover:underline text-xs inline-flex items-center min-h-12 py-3">
                  Terms of Service
                </Link>
                <Link to="/disclaimers" className="text-accent hover:underline text-xs inline-flex items-center min-h-12 py-3">
                  Disclaimers & Regulatory Notices
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-border space-y-3">
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Regulatory status:</strong> AIMediQ™ powered by
            datasyteAI™ is currently in the pre-market / development &amp; validation phase. It is
            not cleared or approved by the FDA or any other regulatory authority, is not
            available for sale, and is not for clinical use. Limited to investigational,
            research, simulation, and training use.
          </p>
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Not medical advice:</strong> Content on this site
            is informational only and is not a substitute for the judgment of a licensed
            healthcare professional or your institution's protocols. In an emergency, contact
            local emergency services.
          </p>
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Forward-looking statements:</strong> Product,
            market, regulatory, and financial statements are forward-looking and subject to
            change. This site is not an offer to sell or a solicitation to buy any security. See
            the{" "}
            <Link to="/disclaimers" className="text-accent hover:underline inline-flex items-center min-h-12 py-1 px-1 -mx-1">
              full disclaimers
            </Link>
            .
          </p>
        </div>
      </div>
    </footer>
  );
};
